import { ScreenContext } from '@/contexts/ScreenContext';
import { useCallback, useContext, useEffect, useState } from 'react';

export const useDragScroll = () => {
    const [node, setNode] = useState<HTMLElement>();
    const [isScrollStart, setIsScrollStart] = useState<boolean>(true);
    const [isScrollEnd, setIsScrollEnd] = useState<boolean>(false);

    const { isMobile } = useContext(ScreenContext);

    const ref = useCallback((nodeEle: HTMLElement) => {
        setNode(nodeEle);
    }, []);

    const handleScroll = (event: Event) => {
        const ele = <HTMLElement>event.target;
        setIsScrollStart(ele.scrollLeft === 0);
        setIsScrollEnd(ele.clientWidth + ele.scrollLeft === ele.scrollWidth);
    };

    const handleMouseDown = useCallback(
        (e: MouseEvent) => {
            if (!node) {
                return;
            }
            const startPos = {
                left: node.scrollLeft,
                top: node.scrollTop,
                x: e.clientX,
                y: e.clientY,
            };

            const handleMouseMove = (e: MouseEvent) => {
                const dx = e.clientX - startPos.x;
                const dy = e.clientY - startPos.y;
                node.scrollTop = startPos.top - dy;
                node.scrollLeft = startPos.left - dx;
                updateCursor(node);
            };

            const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                resetCursor(node);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [node]
    );

    const handleTouchStart = useCallback(
        (e: TouchEvent) => {
            if (!node) {
                return;
            }
            const touch = e.touches[0];
            const startPos = {
                left: node.scrollLeft,
                top: node.scrollTop,
                x: touch.clientX,
                y: touch.clientY,
            };

            const handleTouchMove = (e: TouchEvent) => {
                const touch = e.touches[0];
                const dx = touch.clientX - startPos.x;
                const dy = touch.clientY - startPos.y;
                node.scrollTop = startPos.top - dy;
                node.scrollLeft = startPos.left - dx;
                updateCursor(node);
            };

            const handleTouchEnd = () => {
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
                resetCursor(node);
            };

            document.addEventListener('touchmove', handleTouchMove);
            document.addEventListener('touchend', handleTouchEnd);
        },
        [node]
    );

    const updateCursor = (ele: HTMLElement) => {
        ele.style.cursor = 'grabbing';
        ele.style.userSelect = 'none';
    };

    const resetCursor = (ele: HTMLElement) => {
        ele.style.cursor = 'grab';
        ele.style.removeProperty('user-select');
    };

    useEffect(() => {
        if (!node) {
            return;
        }
        if (isMobile) {
            node.addEventListener('scroll', handleScroll);
            return;
        }
        node.addEventListener('mousedown', handleMouseDown);
        node.addEventListener('touchstart', handleTouchStart);
        node.addEventListener('scroll', handleScroll);
        return () => {
            node.removeEventListener('mousedown', handleMouseDown);
            node.removeEventListener('touchstart', handleTouchStart);
            node.removeEventListener('scroll', handleScroll);
        };
    }, [node, isMobile]);

    return { ref, isScrollStart, isScrollEnd };
};
